import { booleanAttribute, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { firstValueFrom } from 'rxjs';
import {
  IAmanoOneCredentialTypeRequest,
  IAmanoOneCredentialTypeResponse,
  IAmanoOneCredentialSystemCodeResponse,
  IAmanoOneCredentialSystemCodeRequest,
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-amano-one-credential-settings',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    MatOptionModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatListModule,
    MatCheckboxModule,
  ],
  templateUrl: './amano-one-credential-settings.component.html',
  styleUrl: './amano-one-credential-settings.component.scss',
})
export class AmanoOneCredentialSettingsComponent {
  disableAddNewCredentialCode: boolean = true;
  disableRemoveCredentialCode: boolean = true;
  credentialCodeSettingListValue: number[] = [];
  credentialTypeIdInputValue: string = '';
  credentialCodeInputValue: string = '';
  credentialTypeSettingListValue: number = 0;
  credentialTypeInputValue: string = '';
  disableAddNewCredentialType: boolean = true;
  disableRemoveCredentialType: boolean = true;
  credentialTypesList: IAmanoOneCredentialTypeResponse[] = [];
  credentialCodesList: IAmanoOneCredentialSystemCodeResponse[] = [];
  newSystemCodeValue: string = '';
  noSystemCodeChecked: boolean = false;

  //Services
  appDialogService = inject(MessageDialogService);
  appGeneralStateService = inject(AppGeneralStateService);
  #_parcsDataService = inject(ParcsDataService);

  async ngOnInit() {
    this.credentialTypesList = await this.getCredentialTypeList(this.appGeneralStateService.currentLotNumber);
  }

  async onCredentialTypeChange(value: number) {
    const noSystemCodeFlag = this.credentialTypesList.find((type) => type.credentialTypeId === value);
    this.noSystemCodeChecked = noSystemCodeFlag ? noSystemCodeFlag.noSystemCode : false;
    this.credentialTypeSettingListValue = value;
    this.newSystemCodeValue = value.toString();

    this.disableRemoveCredentialType = this.newSystemCodeValue == '';
    this.onMonitorCredentialIdChange();

    if (value != undefined) {
      this.credentialCodesList = await this.getCredentialSystemCodeList(
        this.appGeneralStateService.currentLotNumber,
        value,
      );
    }
  }

  onCredentialCodeChange(credentialCode: string) {
    this.newSystemCodeValue = credentialCode;
    this.disableRemoveCredentialCode = credentialCode == '';
    this.onMonitorCredentialIdChange();
  }

  async onAddCredentialCode() {
    if (this.credentialCodeInputValue.trim() !== '') {
      // Create a value-friendly version (lowercase, replace spaces with underscores)
      const credentialCodeValue = this.credentialCodeInputValue;

      const newCredentialCode: IAmanoOneCredentialSystemCodeRequest = {
        lotNumber: this.appGeneralStateService.currentLotNumber,
        credentialTypeId: +this.newSystemCodeValue,
        credentialCode: this.credentialCodeInputValue.trim(),
      };

      const status: boolean = await this.addNewCredentialSystemCode(newCredentialCode);

      if (status) {
        // Add the new credential code to the list
        this.appDialogService.snackBarTopCenterTimed('Credential Code added successfully.', 3000);
        this.credentialCodeInputValue = ''; //reset input field

        this.credentialCodesList = await this.getCredentialSystemCodeList(
          newCredentialCode.lotNumber,
          newCredentialCode.credentialTypeId,
        );
      } else {
        this.appDialogService.snackBarTopCenterTimed(
          'Failed to add Credential Code.  Insure these values do not already exist.',
          5000,
        );
      }
      // Clear the input field after adding
      this.credentialCodeInputValue = '';
    }
  }

  async onAddCredentialType() {
    if (this.credentialTypeInputValue.trim() !== '') {
      // Create a value-friendly version (lowercase, replace spaces with underscores)
      const credentialTypeValue = this.credentialTypeInputValue;

      const newCredentialType: IAmanoOneCredentialTypeRequest = {
        lotNumber: this.appGeneralStateService.currentLotNumber,
        credentialTypeId: parseInt(this.credentialTypeIdInputValue),
        credentialTypeName: this.credentialTypeInputValue.trim(),
        noSystemCode: this.noSystemCodeChecked,
      };

      const status: boolean = await this.addNewCredentialType(newCredentialType);

      if (status) {
        // Add the new credential type to the list
        this.credentialTypesList = await this.getCredentialTypeList(this.appGeneralStateService.currentLotNumber);

        // Clear the input field after adding
        this.credentialTypeInputValue = '';
        this.credentialTypeIdInputValue = '';
        this.appDialogService.snackBarTopCenterTimed('Credential Type added successfully.', 3000);
      } else {
        this.appDialogService.snackBarTopCenterTimed(
          'Failed to add Credential Type.  Insure these values do not already exist.',
          5000,
        );
      }
    }
  }

  async onRemoveCredentialType() {
    const status: boolean = await this.deleteCredentialTypeItem(
      this.appGeneralStateService.currentLotNumber,
      this.credentialTypeSettingListValue,
    );

    if (status) {
      this.credentialTypesList = await this.getCredentialTypeList(this.appGeneralStateService.currentLotNumber);
      this.appDialogService.snackBarTopCenterTimed('Credential Type removed successfully.', 3000);
    } else {
      this.appDialogService.snackBarTopCenterTimed('Something went wrong. Failed to remove Credential Type.', 5000);
    }
  }

  async onRemoveCredentialCode() {
    const credentialCodeToRemove: number = this.credentialTypeSettingListValue[0];
    const status: boolean = await this.deleteCredentialSystemCodeItem(
      this.appGeneralStateService.currentLotNumber,
      this.newSystemCodeValue,
    );

    if (status) {
      this.credentialCodesList = await this.getCredentialSystemCodeList(
        this.appGeneralStateService.currentLotNumber,
        this.credentialTypeSettingListValue,
      );
      this.appDialogService.snackBarTopCenterTimed('Credential System Code removed successfully.', 3000);
    } else {
      this.appDialogService.snackBarTopCenterTimed(
        'Something went wrong. Failed to remove Credential System Code.',
        5000,
      );
    }
  }

  onMonitorCredentialIdChange() {
    this.disableAddNewCredentialCode =
      this.credentialTypeSettingListValue === 0 || this.credentialCodeInputValue === '';
  }

  onMonitorCredentialTypesChange() {
    this.disableAddNewCredentialType = this.credentialTypeIdInputValue === '' || this.credentialTypeInputValue === '';
  }

  onNoSystemCodeChange(event: any) {}

  async addNewCredentialSystemCode(credentialSystemCodeData: IAmanoOneCredentialSystemCodeRequest): Promise<boolean> {
    let status: boolean = false;

    try {
      status = await firstValueFrom(
        this.#_parcsDataService.AmanoOne_CredentialSystemCode_AddNew(credentialSystemCodeData),
      );
    } catch (err) {
      status = false;
    }
    return status;
  }

  async getCredentialSystemCodeList(
    lotNumber: string,
    credentialTypeId: number,
  ): Promise<IAmanoOneCredentialSystemCodeResponse[]> {
    let credentialSystemCodes: IAmanoOneCredentialSystemCodeResponse[] = [];

    try {
      credentialSystemCodes = await firstValueFrom(
        this.#_parcsDataService.AmanoOne_CredentialSystemCode_GetList(lotNumber, credentialTypeId),
      );
    } catch (err) {
      credentialSystemCodes = [];
    }
    return credentialSystemCodes;
  }

  // Adds a new Credential Type to Amano One PARCS system.
  async addNewCredentialType(credentialData: IAmanoOneCredentialTypeRequest): Promise<boolean> {
    let status: boolean = false;

    try {
      status = await firstValueFrom(this.#_parcsDataService.AmanoOne_CredentialType_AddNew(credentialData));
    } catch (err) {
      status = false;
    }
    return status;
  }

  // Gets the list of Credential Types from Amano One PARCS system.
  async getCredentialTypeList(lotNumber: string): Promise<IAmanoOneCredentialTypeResponse[]> {
    let credentialTypes: IAmanoOneCredentialTypeResponse[] = [];

    try {
      credentialTypes = await firstValueFrom(this.#_parcsDataService.AmanoOne_CredentialType_GetList(lotNumber));
    } catch (err) {
      credentialTypes = [];
    }
    return credentialTypes;
  }

  async deleteCredentialTypeItem(lotNumber: string, credentialTypeId: number): Promise<boolean> {
    let status: boolean = false;

    try {
      status = await firstValueFrom(
        this.#_parcsDataService.AmanoOne_CredentialType_DeleteType(lotNumber, credentialTypeId),
      );
    } catch (err) {
      status = false;
    }
    return status;
  }

  async deleteCredentialSystemCodeItem(lotNumber: string, credentialCode: string): Promise<boolean> {
    let status: boolean = false;

    try {
      status = await firstValueFrom(
        this.#_parcsDataService.AmanoOne_CredentialSystemCode_Delete(lotNumber, credentialCode),
      );
    } catch (err) {
      status = false;
    }
    return status;
  }
}
